<script setup lang="ts">
import { toRef } from 'vue'

import SuccessBanner from '@satch-ui/components/banner/SuccessBanner.vue'
import { buttonVariants } from '@satch-ui/components/button/variants'
import { t } from '@satch-ui/translations/translations'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import { clsx } from 'clsx'
import { useBlockScroll } from 'internal/helper'

import { CartWideIcon, OkayIcon } from '@fond-of/satch-icons'
import { vE2eTestId } from '@fond-of/telemetry'

const props = defineProps<{
  style?: string
  model?: string
  image?: string
  isOpen: boolean
}>()

const emits = defineEmits<{
  (e: 'close'): void
}>()

useBlockScroll(toRef(props, 'isOpen'))
</script>

<template>
  <Transition
    enter-active-class="duration-300 ease-out"
    enter-from-class="transform opacity-0 -translate-y-full md:translate-y-0 md:translate-x-full"
    enter-to-class="opacity-100 translate-y-0 md:translate-x-0"
    leave-active-class="duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 md:translate-x-0"
    leave-to-class="transform opacity-0 -translate-y-full md:translate-y-0 md:translate-x-full"
  >
    <div
      v-if="isOpen"
      v-e2e-test-id="'addToCartSidePanel'"
      class="z-side-panel fixed right-0 top-[3.125rem] w-full max-w-[50rem] overflow-hidden bg-white shadow-xl md:top-0"
    >
      <SuccessBanner
        v-e2e-test-id="'addToCartSidePanelCloseButton'"
        close-icon
        @close="emits('close')"
      >
        <div
          class="flex h-full items-center justify-center space-x-1 md:space-x-2.5"
        >
          <OkayIcon class="w-5.5 h-5.5 md:h-8 md:w-8" />
          <div>{{ t('cart.articleAdded') }}</div>
        </div>
      </SuccessBanner>

      <div class="flex flex-col items-center px-5 md:flex-row md:pl-5 md:pr-10">
        <div class="flex w-full items-center space-x-2 md:space-x-4">
          <div
            class="-ml-5 w-[8.75rem] min-w-[8.75rem] md:ml-0 md:w-[13.75rem] md:min-w-[13.75rem]"
          >
            <img
              class="w-full"
              :src="props.image"
            />
          </div>

          <div class="w-full">
            <div class="font-sans-bold text-md text-black">
              {{ props.model }}
            </div>

            <div class="text-sm text-black">
              {{ t('cart.style') }} {{ props.style }}
            </div>
          </div>
        </div>

        <div
          class="pb-7.5 w-full pt-2.5 text-xs md:w-[13.125rem] md:min-w-[13.125rem] md:py-[3.75rem]"
        >
          <a
            v-e2e-test-id="'addToCartSidePanelToCartButton'"
            :href="localizeUrl('/cart')"
            :class="
              clsx(
                buttonVariants({
                  variant: 'primary',
                  class: 'mb-4 w-full md:mb-5',
                })
              )
            "
          >
            {{ t('cart.toCart') }}

            <CartWideIcon class="size-4" />
          </a>
        </div>
      </div>
    </div>
  </Transition>

  <div
    v-if="isOpen"
    v-e2e-test-id="'sidePanelBackdrop'"
    class="z-side-panel-backdrop fixed inset-0"
    @click="emits('close')"
  />
</template>
